/**
 * WhatsApp API Service
 * 
 * This module handles all WhatsApp-related API calls
 * including message handling, session management, and bot status.
 */

import axios from 'axios';
import API_CONFIG, { getAuthHeaders, getFormHeaders } from './config';

const whatsappAPI = {
  /**
   * Handle incoming WhatsApp message
   * @param {Object} messageData - Message data
   * @returns {Promise<Object>} Bot response
   */
  async handleMessage(messageData) {
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/whatsapp/message`, messageData, {
      headers: API_CONFIG.HEADERS,
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get WhatsApp sessions
   * @param {string} token - JWT access token
   * @returns {Promise<Array>} List of WhatsApp sessions
   */
  async getSessions(token) {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/whatsapp/sessions`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Send WhatsApp message manually
   * @param {string} token - JWT access token
   * @param {string} phoneNumber - Recipient phone number
   * @param {string} message - Message content
   * @returns {Promise<Object>} Send response
   */
  async sendMessage(token, phoneNumber, message) {
    console.log('WhatsApp API: Sending message', { phoneNumber, message, timestamp: new Date().toISOString() });
    
    const formData = new FormData();
    formData.append('phone_number', phoneNumber);
    formData.append('message', message);

    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/whatsapp/send`, formData, {
      headers: getFormHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    
    console.log('WhatsApp API: Message sent successfully', response.data);
    return response.data;
  },

  /**
   * Get WhatsApp service status
   * @param {string} token - JWT access token
   * @returns {Promise<Object>} Service status
   */
  async getStatus(token) {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/whatsapp/status`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get conversation history
   * @param {string} token - JWT access token
   * @param {string} sessionId - Optional session ID
   * @param {string} phoneNumber - Optional phone number
   * @param {number} limit - Maximum number of messages
   * @returns {Promise<Array>} List of conversation messages
   */
  async getConversations(token, sessionId = null, phoneNumber = null, limit = 100) {
    const params = new URLSearchParams();
    if (sessionId) params.append('session_id', sessionId);
    if (phoneNumber) params.append('phone_number', phoneNumber);
    params.append('limit', limit);

    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/whatsapp/conversations?${params}`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get conversation history for a specific session
   * @param {string} token - JWT access token
   * @param {string} sessionId - Session ID
   * @returns {Promise<Array>} List of conversation messages
   */
  async getSessionConversations(token, sessionId) {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/whatsapp/conversations/${sessionId}`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get unique conversations grouped by phone number
   * @param {string} token - JWT access token
   * @param {number} limit - Maximum number of conversations
   * @returns {Promise<Array>} List of unique conversations
   */
  async getUniqueConversations(token, limit = 100) {
    try {
      console.log('Calling unique conversations API...');
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/whatsapp/conversations/unique?limit=${limit}`, {
        headers: getAuthHeaders(token),
        timeout: API_CONFIG.TIMEOUT
      });
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error calling unique conversations API:', error);
      throw error;
    }
  },

  /**
   * Get WhatsApp connection status from the QR service
   * @returns {Promise<Object>} Connection status
   */
  async getConnectionStatus() {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    const response = await axios.get(`${whatsappServiceUrl}/status`, {
      timeout: API_CONFIG.TIMEOUT,
      validateStatus: (status) => status < 500
    });
    return response.data;
  },

  /**
   * Disconnect WhatsApp and prepare for a new QR scan
   * @returns {Promise<Object>} Disconnect response
   */
  async disconnectWhatsApp() {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    const response = await axios.post(`${whatsappServiceUrl}/qr/logout`, {}, {
      timeout: 30000
    });
    return response.data;
  },

  /**
   * Get WhatsApp QR code as base64
   * @param {string} token - JWT access token
   * @returns {Promise<string>} QR code as base64 string
   */
  async getQRCode(token) {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    const response = await axios.get(`${whatsappServiceUrl}/qr`, {
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get WhatsApp QR code image URL
   * @returns {string} QR code image URL
   */
  getQRCodeImageUrl() {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    // Add cache-busting parameter to prevent browser caching
    const timestamp = Date.now();
    return `${whatsappServiceUrl}/qr/latest-image?t=${timestamp}`;
  },

  /**
   * Get WhatsApp QR code as image file
   * @returns {Promise<Blob>} QR code image blob
   */
  async getQRCodeImage() {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    const response = await axios.get(`${whatsappServiceUrl}/qr/latest-image`, {
      responseType: 'blob',
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Refresh WhatsApp QR code
   * @returns {Promise<Object>} Refresh response
   */
  async refreshQRCode() {
    const whatsappServiceUrl = process.env.REACT_APP_WHATSAPP_SERVICE_URL || 'https://khazenly.isnc-logistics.com/qr';
    const response = await axios.post(`${whatsappServiceUrl}/qr/refresh`, {}, {
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  }
};

export { whatsappAPI };
